import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { getInsuranceProtectLandlords } from "../context/functions/AdminFunction";
import InsuranceProtectLandlordsTable from "./insurance-protect-metrics/components/InsuranceProtectLandlordsTable";
import InsuranceProtectDetailsModal from "./insurance-protect-metrics/components/InsuranceProtectDetailsModal";
import "../styles/landlord-metrics.css";
import "../styles/tenants.css";
import { constants } from "../utils/constants";

const LIMIT = constants.LISTING_ROWLIMIT;

const InsuranceProtectMetrics = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState(null);

  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const fetchData = useCallback(async (page, search) => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const res = await getInsuranceProtectLandlords({
        page,
        limit: LIMIT,
        searchText: search,
        signal: abortControllerRef.current.signal,
      });

      if (!res || res.status === "canceled") return;

      if (res.success && res.success_res) {
        setData(res.success_res.data || []);
        setTotal(res.success_res.total || 0);
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (err) {
      if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
        toast.error("Failed to load insurance & Skor Protect metrics");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimerRef.current);
  }, [searchText]);

  useEffect(() => {
    fetchData(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch, fetchData]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  useEffect(() => {
    document.title = "Insurance & Skor Protect Metrics | Admin";
  }, []);

  return (
    <div className="landlord-metrics-container">
      <div className="d-flex flex-column justify-content-between align-items-start mb-4">
        <div className="mb-2">
          <h4 className="fw-bold mb-1" style={{ color: "#2c3e50", fontSize: "22px" }}>
            Insurance & Skor Protect Metrics
          </h4>
          <p className="text-muted small fw-medium mb-0">
            Landlords with confirmed insurances and/or Skor Protect activations across their properties
          </p>
        </div>
      </div>

      <InsuranceProtectLandlordsTable
        data={data}
        loading={loading}
        total={total}
        currentPage={currentPage}
        limit={LIMIT}
        onPageChange={setCurrentPage}
        onViewDetails={(item) => {
          setSelectedLandlord(item);
          setShowModal(true);
        }}
        searchText={searchText}
        onSearchChange={setSearchText}
      />

      <InsuranceProtectDetailsModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedLandlord(null);
        }}
        selectedLandlord={selectedLandlord}
      />
    </div>
  );
};

export default InsuranceProtectMetrics;
